import { ButtonKind, SpsIcon } from "@spscommerce/ds-shared";
import clsx from "clsx";
import nanoid from "nanoid";
import * as React from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";

import { SpsAutocomplete } from "../autocomplete/SpsAutocomplete";
import { SpsButton } from "../button/SpsButton";
import { SpsCard } from "../card/SpsCard";
import * as PropTypes from "../prop-types";
import { usePatchReducer } from "../utils/usePatchReducer";
import { SpsColumnChooserColumn } from "./SpsColumnChooserColumn";

// TODO: Put English strings into i18n system

const propsDoc = {
    maxSelectedColumns: "number",
    unselectedColumns: "ColumnProps[]",
    selectedColumns: "ColumnProps[]",
    onApplyChanges: "(selectedColumns: ColumnProps[]) => void",
};

const propTypes = {
    ...PropTypes.spsGlobalPropTypes,
    maxSelectedColumns: PropTypes.number,
    unselectedColumns: PropTypes.array,
    selectedColumns: PropTypes.array,
    onApplyChanges: PropTypes.fun<(selectedColumns: ColumnProps[]) => void>(),
};

export type SpsColumnChooserProps = PropTypes.InferTS<typeof propTypes, HTMLDivElement>;

export interface ColumnProps {
    name: string;
    value: string;
    mandatory?: boolean;
}

interface Column extends ColumnProps {
    columnId: string;
    label?: string;
}

interface SelectedColumn extends Column {
    index?: number;
    onDelete?: (columnId: string) => void;
}

export function SpsColumnChooser(props: SpsColumnChooserProps) {
    const {
        unselectedColumns,
        selectedColumns,
        maxSelectedColumns,
        onApplyChanges,
        className,
        unsafelyReplaceClassName,
        ...rest
    } = props;
    const [state, patchState] = usePatchReducer({
        isVisible: false,
        unselectedColumns: [
            ...unselectedColumns.map(
                (column): Column => ({
                    ...column,
                    label: column.name,
                    columnId: nanoid(),
                })
            ),
        ],
        selectedColumns: [
            ...selectedColumns.map(
                (column): SelectedColumn => ({
                    ...column,
                    columnId: nanoid(),
                })
            ),
        ],
        searchSelectedColumn: null,
    });

    function onSelectBoxChange(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.value) {
            const selected: SelectedColumn[] = [...state.selectedColumns];
            const unselected: Column[] = [];

            state.unselectedColumns.forEach((column): void => {
                if (event.target.value === column.name) {
                    selected.push({
                        columnId: nanoid(),
                        name: column.name,
                        value: column.value,
                    });
                } else {
                    unselected.push(column);
                }
            });

            patchState({
                selectedColumns: selected,
                unselectedColumns: unselected,
                searchSelectedColumn: null,
            });
        }

        patchState({ searchSelectedColumn: null });
    }

    function removeColumn(columnValue: string): void {
        const selected: SelectedColumn[] = [];
        const unselected: Column[] = [...state.unselectedColumns];

        state.selectedColumns.forEach((column): void => {
            if (columnValue === column.value) {
                unselected.push({
                    columnId: nanoid(),
                    name: column.name,
                    value: column.value,
                });
            } else {
                selected.push(column);
            }
        });

        patchState({ selectedColumns: selected, unselectedColumns: unselected });
    }

    function onDragEnd(result: DropResult): void {
        if (!result.destination) {
            return;
        }

        const selected = [...state.selectedColumns];
        const [removed] = selected.splice(result.source.index, 1);
        selected.splice(result.destination.index, 0, removed);

        patchState({ selectedColumns: selected });
    }

    function applyChanges(): void {
        patchState({ isVisible: false });
        const selectedColumnsCopy = state.selectedColumns.map(
            (column): SelectedColumn => {
                const selectedColumn = { ...column };

                if (selectedColumn.columnId) {
                    delete selectedColumn.columnId;
                }

                return selectedColumn;
            }
        );

        onApplyChanges(selectedColumnsCopy);
    }

    function onVisibilityToggle(): void {
        patchState({ isVisible: !state.isVisible });
    }

    const classes = clsx(state.isVisible && "sps-button--open", className);

    return (
        <div className="sps-column-chooser z-stratum-dropdown" {...rest}>
            <SpsButton
                className={classes}
                aria-label={`${state.isVisible ? "Close" : "Open"} Column Chooser`}
                kind={ButtonKind.ICON}
                icon={SpsIcon.TABLE}
                onClick={onVisibilityToggle}
                data-testid="button"
            />
            {state.isVisible && (
                <SpsCard
                    data-testid="open"
                    footer={() => (
                        <SpsButton
                            aria-label="Apply Changes"
                            kind={ButtonKind.LINK}
                            onClick={applyChanges}
                        >
                            Apply Changes
                        </SpsButton>
                    )}
                >
                    <div className="sps-column-chooser__search-container">
                        <SpsAutocomplete
                            suggestions={state.unselectedColumns.map((c) => c.name)}
                            disabled={
                                (maxSelectedColumns &&
                                    state.selectedColumns.length >= maxSelectedColumns) ||
                                state.selectedColumns.length >= 8
                            }
                            value={
                                state.searchSelectedColumn ? state.searchSelectedColumn.name : ""
                            }
                            onChange={onSelectBoxChange}
                            placeholder="Find Columns to Add"
                        />
                    </div>
                    <div className="sps-column-chooser__selected-title">Selected</div>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="column-chooser">
                            {(provided, _snapshot) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {state.selectedColumns.map((item, index) => (
                                        <SpsColumnChooserColumn
                                            index={index}
                                            key={item.columnId}
                                            columnId={item.columnId}
                                            name={item.name}
                                            value={item.value}
                                            onDelete={removeColumn}
                                            mandatory={item.mandatory}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </SpsCard>
            )}
        </div>
    );
}

Object.assign(SpsColumnChooser, {
    props: propsDoc,
    propTypes,
    displayName: "SpsColumnChooser",
});
